import React from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Table, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from 'components/Common/Breadcrumb';

const InvoiceConfirmation = () => {
  const navigate = useNavigate();

  const mockInvoiceData = {
    invoiceNumber: 'INV-00123',
    date: '2025-08-09',
    supplierId: 'SUP-001',
    manufacturerId: 'MAN-001',
    totalAmount: 1250.75,
  };

  const mockItems = [
    {
      medicineId: 'MED-001',
      medicineName: 'Paracetamol',
      batchNumber: 'BATCH123',
      packing: 'Box of 10 tablets',
      piecesInPacking: 10,
      quantity: 50,
      bonusQuantity: 5,
      rate: 20,
      discountPct: 10,
      discountValue: 100,
      taxAmount: 50,
      netValue: 950,
      margin: 50,
      salesPricePerUnit: 3,
      expiryDate: '2026-08-09',
      updatedStock: 550,
    },
    {
      medicineId: 'MED-002',
      medicineName: 'Amoxicillin',
      batchNumber: 'BATCH456',
      packing: 'Box of 20 capsules',
      piecesInPacking: 20,
      quantity: 30,
      bonusQuantity: 3,
      rate: 15,
      discountPct: 5,
      discountValue: 22.5,
      taxAmount: 30,
      netValue: 457.5,
      margin: 50,
      salesPricePerUnit: 1.125,
      expiryDate: '2026-12-31',
      updatedStock: 630,
    },
  ];

  const supplierName = 'Acme Pharmaceuticals';
  const manufacturerName = 'Global Meds Inc.';

  const totalPurchasedItems = mockItems.length;
  const totalNetAmount = mockItems.reduce((sum, item) => sum + (Number(item.netValue) || 0), 0);
  const totalTax = mockItems.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0);

  return (
    <div className="page-content">
      <Container fluid className="mt-4">
        <Breadcrumb title="Inventory" breadcrumbItem="Purchase Invoice Confirmation" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="bg-success text-white d-flex align-items-center">
                <i className="bx bx-check-circle me-2 bx-sm"></i>
                <h4 className="mb-0">Invoice Saved Successfully</h4>
              </CardHeader>

              <CardBody>
                {/* Invoice Summary */}
                <Row className="mb-4">
                  <Col md={6}>
                    <h5 className="text-primary">
                      <i className="bx bx-file me-2"></i>Invoice Details
                    </h5>
                    <ul className="list-unstyled mb-0">
                      <li>
                        <strong>Invoice #:</strong> {mockInvoiceData.invoiceNumber}
                      </li>
                      <li>
                        <strong>Date:</strong> {new Date(mockInvoiceData.date).toLocaleDateString()}
                      </li>
                      <li>
                        <strong>Supplier:</strong> {supplierName}
                      </li>
                      <li>
                        <strong>Manufacturer:</strong> {manufacturerName}
                      </li>
                      <li>
                        <strong>Total Amount:</strong> ${mockInvoiceData.totalAmount.toFixed(2)}
                      </li>
                    </ul>
                  </Col>
                </Row>

                {/* Items Table */}
                <h5 className="mb-3 text-primary">
                  <i className="bx bx-package me-2"></i>Items Purchased
                </h5>

                <div style={{ overflowX: 'auto' }}>
                  <Table bordered hover responsive size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>
                          <i className="bx bx-capsule me-1"></i>Medicine
                        </th>
                        <th>Batch #</th>
                        <th>Packing</th>
                        <th>Qty (Packs)</th>
                        <th>Bonus</th>
                        <th>Rate</th>
                        <th>Discount</th>
                        <th>Tax</th>
                        <th>Net Value</th>
                        <th>Sales Price/Unit</th>
                        <th>Expiry</th>
                        <th>
                          <i className="bx bx-bar-chart-alt-2 me-1"></i>Updated Stock
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockItems.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {item.medicineName} ({item.medicineId})
                          </td>
                          <td>{item.batchNumber}</td>
                          <td>{item.packing}</td>
                          <td>{item.quantity}</td>
                          <td>{item.bonusQuantity}</td>
                          <td>${item.rate.toFixed(2)}</td>
                          <td>
                            {item.discountPct}% (${item.discountValue.toFixed(2)})
                          </td>
                          <td>${item.taxAmount.toFixed(2)}</td>
                          <td>${item.netValue.toFixed(2)}</td>
                          <td>${item.salesPricePerUnit.toFixed(2)}</td>
                          <td>{new Date(item.expiryDate).toLocaleDateString()}</td>
                          <td>{item.updatedStock} pcs</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Totals */}
                <Row className="mt-4 justify-content-end">
                  <Col xs="12" sm="8" md="6" lg="4">
                    <Card className="shadow-sm">
                      <CardBody className="py-2 px-3">
                        <div className="d-flex justify-content-between mb-2">
                          <strong>
                            <i className="bx bx-list-ul me-1"></i>Items:
                          </strong>
                          <span>{totalPurchasedItems}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <strong>
                            <i className="bx bx-dollar-circle me-1"></i>Net Amount:
                          </strong>
                          <span>${totalNetAmount.toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <strong>
                            <i className="bx bx-receipt me-1"></i>Tax Total:
                          </strong>
                          <span>${totalTax.toFixed(2)}</span>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Actions */}
                <div className="mt-4 text-end">
                  <Button color="primary" onClick={() => navigate('/purchases/invoices/add')}>
                    <i className="bx bx-plus me-1"></i>Create Another Invoice
                  </Button>
                  <Button color="secondary" className="ms-2" onClick={() => navigate('/dashboard')}>
                    <i className="bx bx-home me-1"></i>Back to Dashboard
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default InvoiceConfirmation;
